import { usePrice as swUsePrice } from '@shopware/composables';
import { createSharedComposable } from '@vueuse/core';
import { useRuntimeConfig, useRoute } from '#imports';

// Source of truth for price formatting: BASEPATH (saleschannel URL prefix) → locale + currency.
// Matches the pre-migration hubble behaviour. Independent of Accept-Language and
// Shopware currency custom fields, so it is deterministic per saleschannel.
const currencyMap: Record<string, { locale: string, currencyCode: string }> = {
    '':   { locale: 'de-DE', currencyCode: 'EUR' },
    'at': { locale: 'de-AT', currencyCode: 'EUR' },
    'be': { locale: 'nl-BE', currencyCode: 'EUR' },
    'ch': { locale: 'de-CH', currencyCode: 'CHF' },
    'cz': { locale: 'cs-CZ', currencyCode: 'CZK' },
    'de': { locale: 'de-DE', currencyCode: 'EUR' },
    'dk': { locale: 'da-DK', currencyCode: 'DKK' },
    'ee': { locale: 'et-EE', currencyCode: 'EUR' },
    'es': { locale: 'es-ES', currencyCode: 'EUR' },
    'fi': { locale: 'fi-FI', currencyCode: 'EUR' },
    'fr': { locale: 'fr-FR', currencyCode: 'EUR' },
    'it': { locale: 'it-IT', currencyCode: 'EUR' },
    'lt': { locale: 'lt-LT', currencyCode: 'EUR' },
    'lu': { locale: 'fr-LU', currencyCode: 'EUR' },
    'lv': { locale: 'lv-LV', currencyCode: 'EUR' },
    'nl': { locale: 'nl-NL', currencyCode: 'EUR' },
    'no': { locale: 'nb-NO', currencyCode: 'NOK' },
    'pl': { locale: 'pl-PL', currencyCode: 'PLN' },
    'pt': { locale: 'pt-PT', currencyCode: 'EUR' },
    'se': { locale: 'sv-SE', currencyCode: 'SEK' },
    'si': { locale: 'sl-SI', currencyCode: 'EUR' },
    'sk': { locale: 'sk-SK', currencyCode: 'EUR' },
};

const FALLBACK = currencyMap['']!;

function customUsePrice() {
    const coreUsePrice = swUsePrice();
    const runtimeConfig = useRuntimeConfig();
    const route = useRoute();

    function getUrlLocaleKey(): string {
        const baseURL = runtimeConfig.app?.baseURL;
        if (typeof baseURL === 'string' && baseURL.startsWith('/')) {
            const key = baseURL.slice(1).replace(/\/$/, '').trim();
            if (key in currencyMap) return key;
        }
        const pathFirst = route.path?.split('/').filter(Boolean)[0] ?? '';
        if (pathFirst in currencyMap) return pathFirst;
        return '';
    }

    /**
     * Format price (2) -> $ 2.00
     */
    function getFormattedPrice(value: number | string | undefined, options?: { forceFraction?: boolean, fractionDigits?: number }): string {
        if (typeof value === 'undefined') {
            return '';
        }

        const entry = currencyMap[getUrlLocaleKey()] ?? FALLBACK;
        let minimumFractionDigits = 2;
        let maximumFractionDigits = 2;

        if (options?.fractionDigits !== undefined) {
            minimumFractionDigits = options.fractionDigits;
            maximumFractionDigits = options.fractionDigits;
        } else if (!options?.forceFraction && Number(value) % 1 === 0 && (entry.currencyCode === 'DKK' || entry.currencyCode === 'SEK' || entry.currencyCode === 'NOK')) {
            minimumFractionDigits = 0;
            maximumFractionDigits = 0;
        }

        try {
            return new Intl.NumberFormat(entry.locale, {
                style: 'currency',
                currency: entry.currencyCode,
                minimumFractionDigits,
                maximumFractionDigits,
            }).format(+value);
        } catch {
            // Safety net: should not happen with the hardcoded map, but guarantees no SSR 500
            return new Intl.NumberFormat(FALLBACK.locale, {
                style: 'currency',
                currency: FALLBACK.currencyCode,
                minimumFractionDigits,
                maximumFractionDigits,
            }).format(+value);
        }
    }

    return {
        ...coreUsePrice,
        getFormattedPrice,
    };
}

export const usePrice = createSharedComposable(customUsePrice);
